import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import {
    MtStudyTeamMember,
    MtStudyTeamMemberCreate,
} from "server/models"
import {
    useInput,
    StateType,
} from "client/utils"
import {
    StoreType,
    StudyTeamMemberValidation,
    SaveStudyTeamMember,
    closeModal,
    setStudyTeamMemberEdit,
    setStudyTeamMemberReloadList,
    setStudyTeamMemberReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditTeamMemberProps {
    teamMember: MtStudyTeamMember
}

let isMounted: boolean

const Form = ({
    teamMember,
}: EditTeamMemberProps) => {
    const study = useSelector((state: StoreType) => state.study.studyView)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const { teamMembersFields } = projectSettings.study.teamMembersFieldsAssigned ? projectSettings.study : accountSettings.study

    const [name, setName] = useInput(teamMember.name)
    const [company, setCompany] = useInput(teamMember.company)
    const [title, setTitle] = useInput(teamMember.title)
    const [department, setDepartment] = useInput(teamMember.department)
    const [competence, setCompetence] = useInput(teamMember.competence)
    const [phone, setPhone] = useInput(teamMember.phone)
    const [email, setEmail] = useInput(teamMember.email)
    const [comments, setComments] = useInput(teamMember.comments)

    const [validation, setValidation] = useState<StudyTeamMemberValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newTeamMemberData: MtStudyTeamMemberCreate = {
            studyId: study.id,
            name,
            company,
            title,
            department,
            competence,
            phone,
            email,
            comments,
        }

        SaveStudyTeamMember(teamMember.id, newTeamMemberData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setStudyTeamMemberEdit({
                id: res.newTeamMemberId || teamMember.id,
                ...newTeamMemberData,
            }))

            dispatch(setStudyTeamMemberReloadList(false))
            setTimeout(() => {
                dispatch(setStudyTeamMemberReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setStudyTeamMemberReloadEdit(false))
                setTimeout(() => {
                    dispatch(setStudyTeamMemberReloadEdit(true))
                }, 0)
            }
        })
    }

    const fields: any[] = [
        <FormGroup key="name">
            <Label>{lang.name}</Label>
            <Input type="text" value={name} onChange={setName} />
            <Feedback>{validation && validation.name}</Feedback>
        </FormGroup>,
    ]
    if (teamMembersFields.company) {
        fields.push(
            <FormGroup key="company">
                <Label>{lang.company}</Label>
                <Input value={company} onChange={setCompany} />
                <Feedback>{validation && validation.company}</Feedback>
            </FormGroup>,
        )
    }
    if (teamMembersFields.title) {
        fields.push(
            <FormGroup key="title">
                <Label>{lang.title}</Label>
                <Input type="text" value={title} onChange={setTitle} />
                <Feedback>{validation && validation.title}</Feedback>
            </FormGroup>,
        )
    }
    if (teamMembersFields.department) {
        fields.push(
            <FormGroup key="department">
                <Label>{lang.department}</Label>
                <Input type="text" value={department} onChange={setDepartment} />
                <Feedback>{validation && validation.department}</Feedback>
            </FormGroup>,
        )
    }
    if (teamMembersFields.competence) {
        fields.push(
            <FormGroup key="competence">
                <Label>{lang.competence}</Label>
                <Input type="text" value={competence} onChange={setCompetence} />
                <Feedback>{validation && validation.competence}</Feedback>
            </FormGroup>,
        )
    }
    if (teamMembersFields.phone) {
        fields.push(
            <FormGroup key="phone">
                <Label>{lang.phone}</Label>
                <Input type="text" value={phone} onChange={setPhone} />
                <Feedback>{validation && validation.phone}</Feedback>
            </FormGroup>,
        )
    }
    if (teamMembersFields.email) {
        fields.push(
            <FormGroup key="email">
                <Label>{lang.email}</Label>
                <Input type="text" value={email} onChange={setEmail} />
                <Feedback>{validation && validation.email}</Feedback>
            </FormGroup>,
        )
    }
    if (teamMembersFields.comments) {
        fields.push(
            <FormGroup key="comments">
                <Label>{lang.comments}</Label>
                <Textarea value={comments} onChange={setComments} />
                <Feedback>{validation && validation.comments}</Feedback>
            </FormGroup>,
        )
    }

    const maxColumnLength = fields.length / 2 + (fields.length % 2)

    const fields1 = fields.slice(0, maxColumnLength)
    const fields2 = fields.slice(maxColumnLength)

    return (
        <PageForm>
            <Row>
                {
                    (fields1.length && fields2.length)
                        ? ([
                            <Col key="fields1" xs={6}>
                                {fields1.map((field) => field)}
                            </Col>,
                            <Col key="fields2" xs={6}>
                                {fields2.map((field) => field)}
                            </Col>,
                        ])
                        : (
                            <Col xs={12}>
                                {fields.map((field) => field)}
                            </Col>
                        )
                }
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
